import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Button, Grid, Divider,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import {
  fetchFinancialGoals,
  deleteSuccessFactor,
} from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";
import DetailSection from "../../components/common/DetailSection";
import DetailField from "../../components/common/DetailField";
import ConfirmDialog from "../../components/common/ConfirmDialog";

export default function SmFinancialGoalDetailPage() {
  const { businessDomainId, successFactorId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [data, setData] = useState(null);
  const [targets, setTargets] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const goals = await fetchFinancialGoals(businessDomainId);
      const allGoals = Array.isArray(goals) ? goals : [];
      const found = allGoals.find(
        (g) => String(g.successFactorId) === String(successFactorId)
      );
      setData(found || null);
      // この目標が影響を与える目標（targets）= sourcesに自分を含む他の目標
      const derived = allGoals.filter((g) =>
        String(g.successFactorId) !== String(successFactorId) &&
        (g.sources || []).some((s) => String(s.successFactorId) === String(successFactorId))
      );
      setTargets(derived);
    } catch (e) {
      notify.apiError(e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessDomainId, successFactorId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDelete = async () => {
    try {
      await deleteSuccessFactor(successFactorId);
      notify.success(t("message.delete.success"));
      navigate(`/design/${businessDomainId}/strategy-map/financial-goals`, { replace: true });
    } catch (e) {
      notify.apiError(e);
    }
  };

  if (!data) return null;

  const indicators = data.indicators || [];
  const sources = data.sources || [];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("financialGoal.detail.title")}</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/design/${businessDomainId}/strategy-map/financial-goals/${successFactorId}/edit`)}
          >
            {t("common.edit")}
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setConfirmOpen(true)}
          >
            {t("common.delete")}
          </Button>
        </Box>
      </Box>

      {/* Basic info */}
      <DetailSection>
        <Grid container spacing={2}>
          <Grid size={12}>
            <DetailField label={t("successFactor.name")} value={data.successFactorName} />
          </Grid>
          <Grid size={12}>
            <DetailField label={t("successFactor.description")} value={data.successFactorDescription} />
          </Grid>
        </Grid>
      </DetailSection>

      {/* Indicators */}
      {indicators.length > 0 && (
        <DetailSection sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t("businessKpi.indicatorName")}
          </Typography>
          <Divider sx={{ mb: 1 }} />
          {indicators.map((ind, i) => (
            <Typography key={i} variant="body2">
              ・{ind.indicatorName}（{ind.unit}）
            </Typography>
          ))}
        </DetailSection>
      )}

      {/* Sources: この目標に影響を与える目標 */}
      <DetailSection sx={{ mt: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t("financialGoal.sources.label")}
        </Typography>
        <Divider sx={{ mb: 1 }} />
        {sources.length > 0 ? (
          sources.map((s) => (
            <Typography key={s.successFactorId} variant="body2">
              ・{s.successFactorName}
            </Typography>
          ))
        ) : (
          <Typography variant="body2" color="text.secondary">-</Typography>
        )}
      </DetailSection>

      {/* Targets: この目標が影響を与える目標 */}
      <DetailSection sx={{ mt: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {t("financialGoal.targets.label")}
        </Typography>
        <Divider sx={{ mb: 1 }} />
        {targets.length > 0 ? (
          targets.map((tgt) => (
            <Typography key={tgt.successFactorId} variant="body2">
              ・{tgt.successFactorName}
            </Typography>
          ))
        ) : (
          <Typography variant="body2" color="text.secondary">-</Typography>
        )}
      </DetailSection>

      <Box sx={{ mt: 3 }}>
        <Button
          variant="outlined"
          onClick={() => navigate(`/design/${businessDomainId}/strategy-map/financial-goals`)}
        >
          {t("common.back")}
        </Button>
      </Box>

      <ConfirmDialog
        open={confirmOpen}
        onConfirm={handleDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </Box>
  );
}
